<template>
  <div class="search container container--pt">
    <div class="search__header details__content__box mb-3">
      <div
        class="has-text-centered is-flex is-justify-content-space-between u-mobile-flex-direction-column"
      >
        <div
          class="is-flex is-align-items-center is-justify-content-space-between u-mobile-flex-direction-column"
        >
          <div
            class="main-searchbar__location-input searchbar__input-wrapper mr-4 is-inline-block mb-0 main-searchbar__checkin-input__width-300"
          >
            <LandingLocationSelector
              name="location"
              :input="location ? location.name : ''"
              @selectLocation="selectLocation"
            />
          </div>
          <div
            class="main-searchbar__checkin-input searchbar__input-wrapper mr-4 is-inline-block mb-0 main-searchbar__checkin-input__width-300"
          >
            <DatepickerFoldout
              :checkin="variables.checkin"
              :nights="variables.nights"
              @selectDates="selectDates"
            />
          </div>
          <div
            class="main-searchbar__meta-input searchbar__input-wrapper is-inline-block mr-4"
          >
            <div class="columns is-mobile">
              <div class="column">
                <div class="searchbar__icon">
                  <i
                    class="fas fa-person"
                    :title="$t('searchFilters.numberOfPersons')"
                    aria-hidden="true"
                  ></i>
                </div>
                <input
                  id="adults"
                  type="number"
                  class="searchbar__input"
                  min="1"
                  max="8"
                  :placeholder="String(variables.rooms)"
                  v-model="editableRooms"
                />
              </div>
            </div>
          </div>
          <div
            class="searchbar__input-wrapper is-inline-block mr-4 has-text-left"
          >
            <span class="checkbox--filter">
              <input
                id="hasDirectBookFilter"
                type="checkbox"
                v-model="variables.hasDirectBookFilter"
              />
              <label
                class="mb-0 checkbox--filter--label u-weight--normal"
                for="hasDirectBookFilter"
              >
                {{ $t("directBookingConfirmation") }}
              </label>
            </span>
          </div>
        </div>
        <div
          class="c-button c-button--secondary c-button--no-wrap"
          @click.prevent="toggleFilters"
        >
          {{
            $t(
              filtersOpen
                ? "searchFilters.lessFilters"
                : "searchFilters.moreFilters"
            )
          }}
        </div>
      </div>
      <SearchFilters
        v-if="filtersOpen"
        ref="searchFilters"
        :location="location"
        :variables="variables"
        :filtersOpen="filtersOpen"
        @toggleFilters="toggleFilters"
        @applyFilters="applyFilters"
        @closeFilters="closeFilters"
        @selectedLocation="selectedLocation"
      />
    </div>
    <div class="search__results" ref="searchResultsContainer">
      <div
        class="error-message has-text-red details__content__box"
        v-if="errorMessage"
      >
        <div class="content">
          <h1 class="title is-2">{{ errorMessage }}</h1>
        </div>
      </div>
      <ul class="results-list">
        <li class="results-list__item content">
          <div class="columns is-vcentered is-desktop">
            <div class="column">
              <template v-if="!isLoading">
                {{
                  visibleHotelCount
                    ? $t("loadingCount", {
                        count: visibleHotelCount
                      })
                    : $t("noHotelsFoundTitle")
                }}
              </template>
            </div>
            <div class="column is-narrow">
              <div class="columns is-gapless mb-0">
                <div class="column is-narrow mr-3">
                  <label for="orderBy" class="mb-1 is-block">
                    {{ $t("orderBy.label") }}
                  </label>
                  <span class="select">
                    <select
                      v-model="variables.orderBy"
                      id="orderBy"
                      @change="loadHotels()"
                    >
                      <option value="RECOMMENDED">
                        {{ $t("orderBy.recommended") }}
                      </option>
                      <option value="COSTS">{{ $t("orderBy.costs") }}</option>
                      <option value="RATING">{{ $t("orderBy.rating") }}</option>
                      <option value="DISTANCE">
                        {{ $t("orderBy.distance") }}
                      </option>
                    </select>
                  </span>
                </div>
                <div class="column is-narrow mobile-mt-2">
                  <label for="radius" class="mb-1 is-block">{{
                    $t("distance")
                  }}</label>
                  <span class="select">
                    <select
                      v-model="variables.radius"
                      id="radius"
                      @change="loadHotels()"
                    >
                      <option value="5">
                        {{ $t("searchFilters.distanceToLabel", { n: 5 }) }}
                      </option>
                      <option value="10">
                        {{ $t("searchFilters.distanceToLabel", { n: 10 }) }}
                      </option>
                      <option value="20">
                        {{ $t("searchFilters.distanceToLabel", { n: 20 }) }}
                      </option>
                      <option value="40">
                        {{ $t("searchFilters.distanceToLabel", { n: 40 }) }}
                      </option>
                    </select>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </li>
      </ul>
      <ul class="results-list" v-if="isLoading" aria-busy="true">
        <li v-for="n in 5" :key="n" class="results-list__item">
          <ResultItemSkeleton />
        </li>
      </ul>
      <ul class="results-list" v-else>
        <li
          v-if="hotels?.length === 0 && !errorMessage"
          class="results-list__item mb-3"
        >
          <h5 class="title is-5">{{ $t("noHotelsFoundTitle") }}</h5>
          <p>
            {{ $t("noHotelsFoundMessage") }}
          </p>
        </li>
        <template v-for="(hotel, $index) in hotels" :key="hotel">
          <li
            class="results-list__item"
            :data-ix="$index"
            :data-hotel-name="hotel.name"
            ref="hotelCards"
            :id="`${hotel.hotelId}`"
            :class="{
              'results-list__item--business-select':
                hotel.showBusinessSelectBadge && !hotel.isOverBudget
            }"
          >
            <ResultItem
              :hotel="hotel"
              :location="location"
              @goto-detail="gotoHotelDetail"
              @goto-reservation="gotoHotelReservation"
              @show-marker="showMarker"
              :locationPath="variables.locationPath"
              :feedbackId="feedbackId"
              :collapseOfferless="collapseOfferless"
              :adults="variables.adults"
              :rooms="variables.rooms"
              :checkin="variables.checkin"
              :nights="variables.nights"
              :done="true"
              :showBusinessSelectBadge="hotel.showBusinessSelectBadge"
            />
          </li>
          <hr v-if="hotel.showBusinessSelectBadge && !hotel.isOverBudget" />
        </template>
      </ul>
      <ResultPaging
        v-if="pageCount > 1"
        :pageCount="pageCount"
        :currentPage="variables.page"
        @page="selectPage"
      />
    </div>

    <div
      id="overview-map"
      class="search__map"
      :style="mapHeight ? { height: mapHeight } : {}"
    >
      <SearchLoading
        :loadingData="{ progress: isLoading ? 0 : 100 }"
        v-show="isLoading && !errorMessage"
      />
      <div ref="mapContainer"></div>
    </div>
  </div>
</template>

<script>
import mapboxgl from "!mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import CommonHelper from "@/helpers/common.helper.js";
import ResultItem from "./components/searchresult/ResultItem.vue";
import ResultItemSkeleton from "./components/searchresult/ResultItemSkeleton.vue";
import ResultPaging from "./components/shared/ResultPaging.vue";
import FilterHelper from "@/helpers/filters.helper.js";
import MapsHelper from "@/helpers/maps.helper.js";
import DatepickerFoldout from "./components/landingpage/DatepickerFoldout.vue";
import SearchLoading from "./components/searchresult/SearchLoading.vue";
import HotelHelper from "@/helpers/hotel.helper.js";
import LandingLocationSelector from "./components/landingpage/LandingLocationSelector.vue";
import SearchFilters from "./components/filters/SearchFilters.vue";
import moment from "moment";

export default {
  components: {
    ResultItem,
    ResultItemSkeleton,
    SearchLoading,
    ResultPaging,
    DatepickerFoldout,
    LandingLocationSelector,
    SearchFilters
  },
  name: "SearchResult",
  data() {
    return {
      timeoutCode: null,
      collapseOfferless: false,
      errorMessage: null,
      filtersOpen: false,
      location: null,
      name: null,
      coordinates: null,
      hotels: null,
      hotelCount: 0,
      hotelAvailableCount: 0,
      pageCount: 0,
      map: null,
      filters: [],
      feedbackId: null,
      isLoading: false,
      annotations: [],
      hotelMarkers: [],
      bookingRulesHidden: false,
      mapPopup: null,
      mapHeight: null,
      budget: null,
      companyInfo: null,
      isTonightSearch: false,
      editableRooms: 1,
      editableLocationPath: "",
      editableCheckin: null,
      editableNights: 2,
      variables: {
        page: 1,
        isoLanguage: "nl",
        locationPath: "",
        hotelName: "",
        amount: 25,
        radius: 10,
        stars: [],
        adults: 1,
        rooms: 1,
        refundable: false,
        greenKey: false,
        checkin: new Date().toISOString().split("T")[0],
        nights: 2,
        reviewScore: [],
        amenities: [],
        breakfastIncluded: false,
        hasDirectBookFilter: false,
        orderBy: "RECOMMENDED"
      }
    };
  },
  created() {
    this._initializing = true;
  },
  beforeUnmount() {
    if (this.map) {
      this.map.remove();
    }
    window.removeEventListener("scroll", this._mapScrollHandler);
    window.removeEventListener("resize", this._mapScrollHandler);
  },
  async mounted() {
    this._mapScrollHandler = () => {
      const maxHeight = window.innerHeight - 180;
      const initialHeight = window.innerHeight - 300;
      const newHeight = Math.min(initialHeight + window.scrollY, maxHeight);
      this.mapHeight = newHeight + "px";
      this.$nextTick(() => {
        if (this.map) this.map.resize();
      });
    };
    this._mapScrollHandler();
    window.addEventListener("scroll", this._mapScrollHandler, {
      passive: true
    });
    window.addEventListener("resize", this._mapScrollHandler, {
      passive: true
    });

    const params = { ...this.$route.params };
    this.track("searchResultsPageOpened");

    if (params.checkin) {
      this.variables.checkin = params.checkin;
    }
    if (params.nights) {
      this.variables.nights = params.nights;
    }
    if (params.greenKey) {
      this.variables.greenKey = params.greenKey === "true";
    }
    if (params.adults) {
      this.variables.adults = params.adults;
    }
    if (params.rooms) {
      this.variables.rooms = params.rooms;
    }
    if (params.breakfastIncluded) {
      this.variables.breakfastIncluded = params.breakfastIncluded === "true";
    }
    this.editableRooms = this.variables.rooms;
    this.editableLocationPath = this.variables.locationPath;
    this.editableCheckin = this.variables.checkin;
    this.editableNights = this.variables.nights;
    if (params.orderBy) {
      this.variables.orderBy = params.orderBy;
    }
    if (this.$route.query?.orderBy) {
      this.variables.orderBy = this.$route.query.orderBy;
    }
    // REFACTOR_CHECK: feedbackId is not needed anymore after backend update, but keeping it for now to avoid breaking existing links
    if (this.$route.query?.feedbackId) {
      this.variables.feedbackId = this.$route.query?.feedbackId;
    }

    const locationPath = this.$route.params.locationPath;
    this.variables.locationPath = locationPath;
    mapboxgl.accessToken = this.MAPBOX_ACCESS_TOKEN;
    // if ("collapseOfferless" in localStorage) {
    //   this.collapseOfferless = localStorage.collapseOfferless ? true : false;
    // } else {
    //   this.collapseOfferless = false;
    // }
    this.collapseOfferless = true;
    const companyInfo = await CommonHelper.getCompanyInfo();
    this.companyInfo = companyInfo;
    this.budget = companyInfo.budget;
    let mapContainer = this.$refs.mapContainer;
    //mapContainer.innerHtml = "";
    this.map = MapsHelper.getNewMap(mapContainer);
    await this.loadHotels();
    clearTimeout(this._submitSearchTimeout);
    clearTimeout(this._applyFiltersTimeout);
    this.$nextTick(() => {
      this._initializing = false;
    });
  },
  computed: {
    canEditExpenses() {
      return HotelHelper.canEditExpenses(this.companyInfo);
    },
    canBookOnBehalfOfOthers() {
      return HotelHelper.canBookOnBehalfOfOthers(this.companyInfo);
    },
    // BUG_CHECK: Only shows hotels with offers on current page, not all hotels in the search result
    visibleHotelCount() {
      if (!this.hotels) return 0;
      return this.hotels.filter(
        (h) => HotelHelper.getComparisonDisplayOffer(h) !== null
      ).length;
    }
  },
  watch: {
    collapseOfferless(newValue) {
      localStorage.collapseOfferless = newValue;
    },
    editableRooms() {
      if (this._initializing) return;
      this.debouncedSubmitSearch();
    },
    "variables.hasDirectBookFilter"() {
      if (this._initializing) return;
      this.debouncedSubmitSearch();
    }
  },
  methods: {
    isBreakfastIncludedSelected() {
      return this.variables?.breakfastIncluded ? "true" : "false";
    },
    selectDates(dates) {
      this.editableCheckin = dates.Checkin;
      this.editableNights = dates.Nights;
      if (this._initializing) return;
      this.debouncedSubmitSearch();
    },
    selectLocation(locationItem) {
      this.editableLocationPath = locationItem.locationPath;
      if (this._initializing) return;
      this.debouncedSubmitSearch();
    },
    debouncedSubmitSearch() {
      if (this.timeoutCode) {
        clearTimeout(this.timeoutCode);
        this.timeoutCode = null;
      }
      clearTimeout(this._submitSearchTimeout);
      this._submitSearchTimeout = setTimeout(() => {
        this.submitSearch();
      }, 400);
    },
    submitSearch() {
      this.variables.locationPath =
        this.editableLocationPath || this.variables.locationPath;
      this.variables.checkin = this.editableCheckin || this.variables.checkin;
      this.variables.nights = this.editableNights || this.variables.nights;
      this.variables.rooms = this.editableRooms;
      this.variables.page = 1;
      this.variables.feedbackId = null;
      this.isLoading = true;
      this.hotels = [];
      if (this.$refs.searchFilters) {
        this.$refs.searchFilters.applyFilters();
      } else {
        this.loadHotels();
      }
    },
    openFilters() {
      this.filtersOpen = true;
    },
    closeFilters() {
      this.filtersOpen = false;
    },
    toggleFilters() {
      this.filtersOpen = !this.filtersOpen;
    },

    applyFilters(filters) {
      this.filters = filters;
      this.variables.stars = FilterHelper.getStars(filters.stars);
      this.variables.hotelName = filters.hotelName;
      this.variables.radius = filters.radius;
      this.variables.refundable = filters.refundable;
      this.variables.greenKey = filters.greenKey;
      this.variables.reviewScore = FilterHelper.getReviewScore(filters.score);
      this.variables.amenities = [];
      for (let key in filters.amenities) {
        if (filters.amenities[key]) {
          this.variables.amenities.push(key);
        }
      }

      if (filters.checkin) {
        this.variables.checkin = moment(filters.checkin).format("YYYY-MM-DD");
      }
      this.variables.nights = filters.nights;
      this.variables.adults = filters.adults;
      this.variables.rooms = filters.rooms;
      this.variables.feedbackId = null;

      // Cancel any in-flight polling
      if (this.timeoutCode) {
        clearTimeout(this.timeoutCode);
        this.timeoutCode = null;
      }

      // Debounce the actual API call
      clearTimeout(this._applyFiltersTimeout);
      this._applyFiltersTimeout = setTimeout(() => {
        this.isLoading = true;
        this.hotels = [];
        let selectedFilters = { ...this.variables };
        delete selectedFilters.feedbackId;
        delete selectedFilters.isoLanguage;
        selectedFilters.amenities = selectedFilters.amenities.join(", ");
        selectedFilters.reviewScore = selectedFilters.reviewScore.join(", ");
        selectedFilters.stars = selectedFilters.stars.join(", ");

        this.track("submitSearchFilters", selectedFilters);
        this.loadHotels();
      }, 300);
    },

    loadMap(location) {
      if (!this.map.isStyleLoaded()) {
        this.map.once("load", () => this.loadMap(location));
        return;
      }

      const myCenter = [location.longitude, location.latitude];
      this.map.setCenter(myCenter);

      this.hotelMarkers = this.hotels.map((hotel) =>
        MapsHelper.getMarkerFromHotel(hotel)
      );
      const hotelSource = MapsHelper.getSource(this.hotelMarkers);
      const layer = MapsHelper.getLayer("hotelLayer", "hotels");
      try {
        if (this.map.getLayer("hotelLayer")) {
          this.map.removeLayer("hotelLayer");
        }
        if (this.map.getSource("hotels")) {
          this.map.removeSource("hotels");
        }
      } catch (e) {
        // Style not yet fully initialised — skip removal
      }

      this.map.addSource("hotels", hotelSource);
      this.map.addLayer(layer);
      if (this.hotelMarkers.length > 1) {
        var bounds = new mapboxgl.LngLatBounds();
        this.hotelMarkers.forEach(function (feature) {
          bounds.extend(feature.geometry.coordinates);
        });

        this.map.fitBounds(bounds);
      }
      const el = document.createElement("div");
      el.className = "searched-marker";
      new mapboxgl.Marker(el).setLngLat(myCenter).addTo(this.map);
      this.map.off("click", "hotelLayer", this.handleMarkerClick);
      this.map.on("click", "hotelLayer", this.handleMarkerClick);
    },
    handleMarkerClick(e) {
      let selectedHotelId = e.features[0].properties.hotelId;
      const hotelCards = this.$refs.hotelCards;
      const selectedHotelCard = hotelCards.find((e) => e.id == selectedHotelId);
      const selectedHotel = this.hotels.find(
        (h) => h.hotelId === selectedHotelId
      );
      this.track("hotelMarkerClickSearchResult", {
        hotelName: selectedHotel.name,
        hotelCity: selectedHotel.city,
        hotelPostalCode: selectedHotel.postalCode,
        hotelId: selectedHotel.hotelId,
        hotelStreet: selectedHotel.street
      });

      window.scrollTo({
        top:
          selectedHotelCard.getBoundingClientRect().top + window.scrollY - 200,
        left: 0,
        behavior: "smooth"
      });

      selectedHotelCard.classList.add("selected-card");
      setTimeout(() => {
        selectedHotelCard.classList.remove("selected-card");
      }, 5000);

      const coordinates = e.features[0].geometry.coordinates.slice();
      // Ensure that if the map is zoomed out such that multiple
      // copies of the feature are visible, the popup appears
      // over the copy being pointed to.
      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }
      let description = MapsHelper.getPopupDescription(selectedHotel, this.$t);
      if (this.mapPopup) {
        this.mapPopup.remove();
      }
      this.mapPopup = new mapboxgl.Popup()
        .setLngLat(coordinates)
        .setHTML(description);
      this.mapPopup.addTo(this.map);
    },
    selectPage(page) {
      this.track("gotoPageSearchResult", {
        page: page
      });
      this.variables.page = page;
      this.variables.feedbackId = this.feedbackId;

      this.loadHotels(true);
    },
    gotoHotelDetail(hotelId) {
      if (this.timeoutCode) {
        // Stop polling if detailpage is loading
        clearTimeout(this.timeoutCode);
      }
      this.track("gotoHotelDetailSearchResults", {
        locationPath: this.variables.locationPath,
        hotelId: hotelId,
        checkin: this.variables.checkin,
        nights: this.variables.nights,
        adults: this.variables.adults,
        refundable: this.variables.refundable,
        greenKey: this.variables.greenKey,
        rooms: this.variables.rooms,
        breakfastIncluded: this.isBreakfastIncludedSelected()
      });
      this.$router.push({
        name: this.PAGES.HotelDetail,
        params: {
          locationPath: this.variables.locationPath,
          hotelId: hotelId,
          checkin: this.variables.checkin,
          nights: this.variables.nights,
          adults: this.variables.adults,
          refundable: this.variables.refundable,
          greenKey: this.variables.greenKey,
          rooms: this.variables.rooms,
          feedbackId: this.variables.page === 1 ? this.feedbackId : null,
          breakfastIncluded: this.isBreakfastIncludedSelected()
        }
      });
    },
    showMarker(selectedHotelId) {
      const hotel = this.hotels.find((h) => h.hotelId === selectedHotelId);
      const lngLat = new mapboxgl.LngLat(hotel.longitude, hotel.latitude);

      const description = MapsHelper.getPopupDescription(hotel, this.$t);
      if (this.mapPopup) {
        this.mapPopup.remove();
      }
      this.mapPopup = new mapboxgl.Popup()
        .setLngLat(lngLat)
        .setHTML(description);
      this.mapPopup.addTo(this.map);
    },
    async gotoHotelReservation(hotelId) {
      if (this.timeoutCode) {
        // Stop polling if detailpage is loading
        clearTimeout(this.timeoutCode);
      }
      this.track("gotoHotelReservationSearchResults", {
        locationPath: this.variables.locationPath,
        hotelId: hotelId,
        checkin: this.variables.checkin,
        nights: this.variables.nights,
        adults: this.variables.adults,
        refundable: this.variables.refundable,
        greenKey: this.variables.greenKey,
        rooms: this.variables.rooms,
        breakfastIncluded: this.isBreakfastIncludedSelected()
      });

      const companyInfo = await CommonHelper.getCompanyInfo();

      const hotel = this.hotels.find((h) => h.hotelId === hotelId);
      const rawOffer = hotel?.displayOffer ?? null;

      let flatOffer = null;
      if (rawOffer) {
        const { bedTypes, ...offerWithoutBedTypes } = rawOffer;
        const firstBedType = bedTypes ? Object.values(bedTypes)[0] : null;
        flatOffer = {
          ...offerWithoutBedTypes,
          description: firstBedType?.description || null,
          priceCheckHref: firstBedType?.priceCheckHref || null
        };
      }

      const offerData = {
        offer: flatOffer,
        companyCode: companyInfo?.companyCode || null,
        feedbackIdGuid: this.feedbackId || null,
        checkin: this.variables.checkin,
        nights: parseInt(this.variables.nights) || 1,
        adults: parseInt(this.variables.adults) || 1,
        rooms: parseInt(this.variables.rooms) || 1,
        locationPath: this.variables.locationPath,
        hotelId: hotelId,
        greenKey: this.variables.greenKey
      };

      this.$router.push({
        name: this.PAGES.HotelReservationForm,
        params: {
          locationPath: this.variables.locationPath,
          hotelId: hotelId,
          checkin: this.variables.checkin,
          nights: this.variables.nights || 1,
          adults: this.variables.adults || 1,
          rooms: this.variables.rooms || 1,
          refundable: rawOffer?.isRefundable ?? true,
          greenKey: this.variables.greenKey,
          breakfastIncluded: rawOffer?.isBreakfastIncluded ?? false
        },
        state: { offerData }
      });
    },
    gotoCityReservation() {
      this.$router.push({
        name: this.PAGES.CityReservationForm,
        params: {
          city: this.variables.locationPath,
          checkin: this.variables.checkin,
          nights: this.variables.nights,
          adults: this.variables.adults,
          rooms: this.variables.rooms,
          latitude: this.location.latitude,
          refundable: this.variables.refundable,
          greenKey: this.variables.greenKey,
          longitude: this.location.longitude
        }
      });
    },

    async loadHotels(skipPolling = false) {
      if (this.timeoutCode) {
        clearTimeout(this.timeoutCode);
        this.timeoutCode = null;
      }
      try {
        MapsHelper.clearMap(
          this.map,
          this.map.getStyle().layers,
          this.map.getStyle().sources
        );
      } catch (e) {
        /// Map is empty
      }
      this.errorMessage = null;
      this.isLoading = true;
      this.isTonightSearch = moment(this.variables.checkin).isSame(
        new Date(),
        "day"
      );

      const url = `${this.AVAILABILITY_API_URL}/hotel/getwithpricesbygeopath`;
      let { data, status } = await this.axios.post(url, this.variables);
      if (status !== 200) {
        this.isLoading = false;
        this.errorMessage = this.$t("serverErrorTryAgain");
        this.hotels = [];
        return;
      }
      this.feedbackId = data.feedbackId;

      const hotelAvailableCount = Number(data.hotelAvailableCount);
      const responsePageCount = Number(data.pageCount);

      this.variables.feedbackId = data.feedbackId;
      this.location = data.geoPath;
      this.hotelCount = data.hotelCount;
      let hotels = data.hotels;

      this.hotelAvailableCount = Number.isFinite(hotelAvailableCount)
        ? hotelAvailableCount
        : 0;
      this.pageCount = Number.isFinite(responsePageCount)
        ? responsePageCount
        : Math.ceil(this.hotelAvailableCount / this.variables.amount);
      hotels.forEach((h) => {
        let resolved = this.$router.resolve({
          name: this.PAGES.HotelDetail,
          params: {
            locationPath: this.variables.locationPath,
            hotelId: h.hotelId,
            checkin: this.variables.checkin,
            nights: this.variables.nights,
            adults: this.variables.adults,
            refundable: this.variables.refundable,
            greenKey: this.variables.greenKey,
            rooms: this.variables.rooms,
            feedbackId: this.feedbackId,
            breakfastIncluded: this.isBreakfastIncludedSelected()
          }
        });

        h.link = resolved.href;

        let resolvedReservation = this.$router.resolve({
          name: this.PAGES.HotelReservationForm,
          params: {
            locationPath: this.variables.locationPath,
            hotelId: h.hotelId,
            checkin: this.variables.checkin,
            nights: this.variables.nights,
            adults: this.variables.adults,
            refundable: this.variables.refundable,
            greenKey: this.variables.greenKey,
            rooms: this.variables.rooms,
            feedbackId: this.feedbackId
          }
        });
        h.reservationLink = resolvedReservation.href;

        const displayOffer = HotelHelper.getComparisonDisplayOffer(h);

        h.showBusinessSelectBadge = h.isBusinessSelect && displayOffer !== null;

        h.isOverBudget =
          this.budget &&
          displayOffer &&
          HotelHelper.getBarePricePerNight(
            displayOffer,
            this.variables.nights
          ) > this.budget &&
          !this.canBookOnBehalfOfOthers;
      });

      // Sort hotels: business select first (unless collapsed), over-budget last
      hotels.sort((a, b) => {
        const aDisplayOffer = HotelHelper.getComparisonDisplayOffer(a);
        const bDisplayOffer = HotelHelper.getComparisonDisplayOffer(b);
        const aIsCollapsed = aDisplayOffer == null && this.collapseOfferless;
        const bIsCollapsed = bDisplayOffer == null && this.collapseOfferless;

        const aOverBudget =
          this.budget &&
          aDisplayOffer &&
          HotelHelper.getBarePricePerNight(
            aDisplayOffer,
            this.variables.nights
          ) > this.budget &&
          !this.canBookOnBehalfOfOthers;
        const bOverBudget =
          this.budget &&
          bDisplayOffer &&
          HotelHelper.getBarePricePerNight(
            bDisplayOffer,
            this.variables.nights
          ) > this.budget &&
          !this.canBookOnBehalfOfOthers;

        // No-availability items go to the very bottom
        if (aIsCollapsed && !bIsCollapsed) return 1;
        if (!aIsCollapsed && bIsCollapsed) return -1;

        // Business select is always priority #1
        if (a.isBusinessSelect && !b.isBusinessSelect) return -1;
        if (!a.isBusinessSelect && b.isBusinessSelect) return 1;

        // Over-budget items go after valid hotels but before no-availability
        if (aOverBudget && !bOverBudget) return 1;
        if (!aOverBudget && bOverBudget) return -1;

        // Ordering beyond business-select / over-budget is delegated to the
        // backend list order (HotelOrderingHelper), preserved by this stable
        // sort. The backend now demotes Bar agreements out of the recommended
        // tier to match the previous frontend isRecommendedHotel ordering, so
        // re-applying the recommended / price-agreement tiers here is redundant.
        return 0;
      });

      this.hotels = hotels;
      this.coordinates = `${this.location.longitude}${this.location.latitude}`;
      this.name = this.location.name;

      if (data.feedbackId && data.feedbackId != this.$route.params.feedbackId) {
        this.$router.replace({
          name: this.PAGES.SearchResult,
          params: {
            locationPath: this.variables.locationPath,
            checkin: this.variables.checkin,
            nights: this.variables.nights,
            adults: this.variables.adults,
            rooms: this.variables.rooms,
            refundable: this.variables.refundable,
            greenKey: this.variables.greenKey,
            feedbackId: data.feedbackId
          },
          query: {
            orderBy: this.variables.orderBy
          }
        });
      }

      this.loadMap(this.location);

      if (
        !skipPolling &&
        data.hotelCount > 0 &&
        data.progress !== undefined &&
        data.progress < 100
      ) {
        this.timeoutCode = setTimeout(() => {
          this.loadHotels();
        }, 500);
      } else {
        this.isLoading = false;
      }
    },
    selectedLocation(locationPath) {
      this.variables.locationPath = locationPath;
      this.$router.replace({
        name: this.PAGES.SearchResult,
        params: {
          locationPath: this.variables.locationPath,
          checkin: this.variables.checkin,
          nights: this.variables.nights,
          adults: this.variables.adults,
          rooms: this.variables.rooms,
          refundable: this.variables.refundable,
          greenKey: this.variables.greenKey,
          feedbackId: this.feedbackId
        },
        query: {
          orderBy: this.variables.orderBy
        }
      });
    }
  }
};
</script>

<style lang="scss">
@use "sass:color";
body {
  background: color.adjust(#fff, $lightness: -3%);
}

#progress-indicator {
  position: fixed;
  bottom: 10px;
  left: 10px;
  background: orange;
  color: white;
  padding: 10px 5px;
}

.main-searchbar__checkin-input {
  &__width-300 {
    width: 300px;

    @media screen and (max-width: 1406px) {
      width: auto;
    }
  }
}

@media screen and (max-width: 988px) {
  .searchbar__input-wrapper {
    width: 100%;
    margin-bottom: 1rem !important;
    margin-right: 0 !important;
  }

  .u-mobile-flex-direction-column {
    flex-direction: column;
  }
}

@media (max-width: 768px) {
  .mobile-mt-2 {
    margin-top: 0.5rem !important;
  }
}

.more-filters {
  text-decoration: underline;
  cursor: pointer;
}
</style>
